import { Table, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { EvaluationAuthorCategoryOverview, EvaluationCategory } from '@/module/Evaluation/model/EvaluationTypes';

import { Content } from '@/core/components';

import { Id, formatDecimal } from '@feature/utils';

interface EvaluationReportTableProps {
  category: EvaluationCategory;
  overview: EvaluationAuthorCategoryOverview;

  evaluationId: Id;
}

export function EvaluationReportTable({ category, overview, evaluationId }: EvaluationReportTableProps) {
  const {
    data,
    categoryWeight,
    totalAuthorPoints,
    totalHeadOfDisciplinePoints,
    totalHeadOfDepartmentPoints,
    totalAuthorCategoryPoints,
    totalHeadOfDisciplineCategoryPoints,
    totalHeadOfDepartmentCategoryPoints,
  } = overview;
  const { t } = useTranslation();

  return (
    <Content>
      <Text
        fw="500"
        size="lg"
      >
        {t(`evaluation.category.${category}`)}
      </Text>
      <Table
        style={{ borderRadius: '12px' }}
        striped
        highlightOnHover
        withTableBorder
        withColumnBorders
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('evaluation.table.index')}</Table.Th>
            <Table.Th>{t('evaluation.table.title')}</Table.Th>
            <Table.Th>{t('evaluation.table.legend')}</Table.Th>
            <Table.Th>{t('evaluation.table.points')}</Table.Th>
            <Table.Th>{t('evaluation.table.headOfDisciplinePoints')}</Table.Th>
            <Table.Th>{t('evaluation.table.headOfDepartmentPoints')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map(element => (
            <Table.Tr key={element.criteria.orderNo}>
              <Table.Td>
                <Link
                  search
                  to="/evaluation/$evaluationId/report/gather/$orderNo"
                  params={{ evaluationId: String(evaluationId), orderNo: String(element.criteria.orderNo) }}
                >
                  {element.criteria.orderNo}
                </Link>
              </Table.Td>
              <Table.Td>{element.criteria.title}</Table.Td>
              <Table.Td>{element.criteria.legend}</Table.Td>
              <Table.Td ta="right">{element.formattedAuthorPoints}</Table.Td>
              <Table.Td ta="right">{element.formattedHeadOfDisciplinePoints}</Table.Td>
              <Table.Td ta="right">{element.formattedHeadOfDepartmentPoints}</Table.Td>
            </Table.Tr>
          ))}
          <Table.Tr>
            <Table.Td
              colSpan={3}
              fw="500"
            >
              Total autor
            </Table.Td>
            <Table.Td
              colSpan={3}
              ta="right"
            >
              {formatDecimal(totalAuthorPoints)}
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td
              colSpan={3}
              fw="500"
            >
              Total ponderat (punctaj x coseficientul de ponderare conform funcției didactice - {categoryWeight})
            </Table.Td>
            <Table.Td
              colSpan={3}
              ta="right"
            >
              {formatDecimal(totalAuthorCategoryPoints)}
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td colSpan={6} />
          </Table.Tr>
          <Table.Tr>
            <Table.Td
              colSpan={3}
              fw="500"
            >
              Total șef de disciplină
            </Table.Td>
            <Table.Td
              colSpan={3}
              ta="right"
            >
              {formatDecimal(totalHeadOfDisciplinePoints)}
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td
              colSpan={3}
              fw="500"
            >
              Total ponderat (punctaj x coeficientul de ponderare conform funcției didactice - {categoryWeight})
            </Table.Td>
            <Table.Td
              colSpan={3}
              ta="right"
            >
              {formatDecimal(totalHeadOfDisciplineCategoryPoints)}
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td colSpan={6} />
          </Table.Tr>
          <Table.Tr>
            <Table.Td
              colSpan={3}
              fw="500"
            >
              Total director departament
            </Table.Td>
            <Table.Td
              colSpan={3}
              ta="right"
            >
              {formatDecimal(totalHeadOfDepartmentPoints)}
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td
              colSpan={3}
              fw="500"
            >
              Total ponderat (punctaj x coeficientul de ponderare conform funcției didactice - {categoryWeight})
            </Table.Td>
            <Table.Td
              colSpan={3}
              ta="right"
            >
              {formatDecimal(totalHeadOfDepartmentCategoryPoints)}
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Content>
  );
}
